<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import BacklinkRow from "@/components/backlinks/partials/BacklinkRow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import BacklinkAuthorityTable from "@/components/backlinks/partials/BacklinkAuthorityTable.vue";
import {computed, ref, watch} from "vue";
import {useStore} from "vuex";
import {getBacklinks} from "@/composables/useBacklinks";
import {useInfiniteScroll} from "@vueuse/core";

const store = useStore();

const mode = computed(() => store.getters['backlinks/mode']);
const search = computed(() => store.getters['backlinks/search']);
const orderBy = computed(() => store.getters['backlinks/orderBy']);
const orderDirection = computed(() => store.getters['backlinks/orderDirection']);
const activeTab = computed(() => store.getters['backlinks/activeTab']);
const advancedMode = computed(() => mode.value === 'advanced');

const changeOrderBy = (name) => {
  store.dispatch('backlinks/setOrderBy', name);
};

const { data: backlinksData, isLoading } = getBacklinks(activeTab);

const tableBody = ref(null);
const visibleRows = ref(50);

const backlinks = computed(() =>
  backlinksData.value
    .filter(backlink => backlink.url.toLowerCase().includes(search.value.toLowerCase()))
    .sort(function (a, b) {
      if (orderDirection.value === 'asc') {
        return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
      }

      return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
    })
    .slice(0, visibleRows.value)
)

useInfiniteScroll(
  computed(() => tableBody.value?.$el),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 200,
  }
)

watch(
  activeTab,
  () => visibleRows.value = 50
)

</script>

<template>
  <LoadingBlock v-if="isLoading" />

  <BacklinkAuthorityTable v-else-if="activeTab === 'authority'" />

  <DataTable v-else>
    <TableHeaderRow :class="advancedMode ? 'tw-grid-cols-21' : 'tw-grid-cols-16'">
      <TableHeaderCell
        v-if="!advancedMode"
        col-span="4"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="domain"
        @order-changed="changeOrderBy"
      >
        <span>Domain</span>
      </TableHeaderCell>

      <TableHeaderCell
        :col-span="advancedMode ? 5 : 6"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="url"
        @order-changed="changeOrderBy"
      >
        <span>URL</span>
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        :col-span="2"
        centered
        name="domainAuthority"
        @order-changed="changeOrderBy"
      >
        <span>Domain Authority</span>
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        :col-span="2"
        centered
        name="pageAuthority"
        @order-changed="changeOrderBy"
      >
        <span>Page Authority</span>
      </TableHeaderCell>

      <TableHeaderCell
        :col-span="advancedMode ? 3 : 2"
        :order-by="orderBy"
        :order-direction="orderDirection"
        :name="activeTab === 'lost' ? 'lost' : 'found'"
        centered
        @order-changed="changeOrderBy"
      >
        <span v-if="activeTab === 'lost'">Date Lost</span>
        <span v-else>Date Found</span>
      </TableHeaderCell>

      <TableHeaderCell
        v-if="advancedMode"
        :col-span="advancedMode ? 2 : 1"
        :order-by="orderBy"
        :order-direction="orderDirection"
        centered
        name="followType"
        @order-changed="changeOrderBy"
      >
        <span>Follow Type</span>
      </TableHeaderCell>

      <TableHeaderCell
        v-if="advancedMode"
        :col-span="5"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="anchorText"
        @order-changed="changeOrderBy"
      >
        <span>Anchor Link</span>
      </TableHeaderCell>

      <TableHeaderCell
        v-if="advancedMode"
        :col-span="2"
        :order-by="orderBy"
        :order-direction="orderDirection"
        centered
        name="linkType"
        @order-changed="changeOrderBy"
      >
        <span>Link Type</span>
      </TableHeaderCell>
    </TableHeaderRow>

    <TableBody
      v-if="backlinks?.length > 0"
      ref="tableBody"
      class="tw-max-h-[70vh]"
    >
      <BacklinkRow
        v-for="backlink in backlinks"
        :key="backlink.url"
        :backlink="backlink"
      />
    </TableBody>

    <ZeroContent v-if="!backlinks || !backlinks.length">
      Looks like there are no <br>backlinks to display.
    </ZeroContent>
  </DataTable>
</template>
